import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface Subject {
  id: number;
  subject_name: string;
  subject_code: string;
}

const ManageSubjects: React.FC = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  
  // Form State
  const [subjectName, setSubjectName] = useState('');
  const [subjectCode, setSubjectCode] = useState('');

  const fetchSubjects = async () => {
    try {
      const res = await api.get('/subjects');
      if (res.data.success) setSubjects(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  const handleOpenModal = (subject?: Subject) => {
    if (subject) {
      setEditingSubject(subject);
      setSubjectName(subject.subject_name);
      setSubjectCode(subject.subject_code || '');
    } else {
      setEditingSubject(null);
      setSubjectName('');
      setSubjectCode('');
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { subject_name: subjectName, subject_code: subjectCode };
    
    try {
      if (editingSubject) {
        await api.put(`/subjects/${editingSubject.id}`, payload);
      } else {
        await api.post('/subjects', payload);
      }
      setIsModalOpen(false);
      fetchSubjects();
    } catch (err) {
      console.error("Error saving subject", err);
      alert("Failed to save subject.");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this subject?")) return;
    try {
      await api.delete(`/subjects/${id}`);
      fetchSubjects();
    } catch (err) {
      console.error("Error deleting subject", err);
      alert("Failed to delete subject.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      
      {/* Header controls banner */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-page-header mb-1">Manage Subjects</h1>
          <p className="text-helper">Configure the active syllabus subject courses offered at your institutional curriculum level.</p>
        </div>
        <button 
          onClick={() => handleOpenModal()} 
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl transition-all shadow-sm text-nav text-center whitespace-nowrap"
        >
          + Add Subject
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent"></div>
          </div>
        ) : (
          <>
            {/* A. Responsive Card view for Mobile viewports */}
            <div className="block md:hidden p-4 space-y-3.5">
              {subjects.length === 0 ? (
                <p className="text-center text-helper italic text-slate-400 py-6">No subjects configured.</p>
              ) : (
                subjects.map((subject) => (
                  <div key={subject.id} className="p-4 rounded-xl border border-slate-100 bg-white space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <span className="font-bold text-card-title text-slate-900">{subject.subject_name}</span>
                        <p className="text-[10px] text-slate-500">Subject Code: <strong className="text-slate-700">{subject.subject_code || '-'}</strong></p>
                      </div>
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                        Active
                      </span>
                    </div>

                    <div className="flex justify-end gap-3 pt-2 border-t border-slate-50 text-nav">
                      <button onClick={() => handleOpenModal(subject)} className="text-indigo-600 font-semibold hover:underline">Edit</button>
                      <button onClick={() => handleDelete(subject.id)} className="text-rose-500 font-semibold hover:underline">Remove</button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* B. Wide Table view for Desktop/Tablet viewports */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Subject Name</th>
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Code</th>
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="p-4 text-center text-card-title text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {subjects.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="p-6 text-center text-helper italic text-slate-400">No subjects configured.</td>
                    </tr>
                  ) : (
                    subjects.map((subject) => (
                      <tr key={subject.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="p-4 text-table-body font-bold text-slate-900">{subject.subject_name}</td>
                        <td className="p-4 text-table-body text-slate-500">{subject.subject_code || '-'}</td>
                        <td className="p-4">
                          <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border border-emerald-150">Active</span>
                        </td>
                        <td className="p-4 text-center">
                          <button onClick={() => handleOpenModal(subject)} className="text-indigo-600 font-semibold hover:underline mr-4 text-nav">Edit</button>
                          <button onClick={() => handleDelete(subject.id)} className="text-rose-500 font-semibold hover:underline text-nav">Remove</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal Form */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-slate-100">
            <h2 className="text-section-header mb-4">{editingSubject ? 'Edit Subject Details' : 'Add Subject Course'}</h2>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Subject Name</label>
                <input 
                  type="text" 
                  value={subjectName} 
                  onChange={e => setSubjectName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" 
                  required 
                />
              </div>
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Subject Code</label>
                <input 
                  type="text" 
                  value={subjectCode} 
                  onChange={e => setSubjectCode(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" 
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-nav font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                <button type="submit" className="px-5 py-2 text-nav font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-sm transition-colors">Save Subject</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSubjects;
